import React, { useState, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useToast } from '@/hooks/use-toast'
import { Plus, Settings, Users, Building2, Sliders, Edit, Trash2, Type, CircleDot, List, Hash, Calendar, Mail, Link as LinkIcon, CheckSquare } from 'lucide-react'
import { apiService, CustomField, CustomFieldOption, CompanyCustomField, CompanyCustomFieldOption } from '@/services/api'
import { useAuthStore } from '@/store/authStore'

export default function CustomFields() {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const [activeTab, setActiveTab] = useState<'contacts' | 'companies'>('contacts')
  
  // Référence pour le focus automatique
  const firstFieldRef = useRef<HTMLInputElement>(null)
  
  // États pour les champs personnalisés existants
  const [contactFields, setContactFields] = useState<(CustomField & { usage_count: number, options?: CustomFieldOption[] })[]>([])
  const [companyFields, setCompanyFields] = useState<(CompanyCustomField & { usage_count: number, options?: CompanyCustomFieldOption[] })[]>([])
  const [loading, setLoading] = useState(true)
  
  // États pour le modal de création/édition de champ personnalisé
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [fieldToEdit, setFieldToEdit] = useState<(CustomField | CompanyCustomField) & { usage_count: number, options?: any[] } | null>(null)
  const [createForm, setCreateForm] = useState({
    name: '',
    appliesTo: '',
    fieldType: '',
    required: false
  })
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({})
  
  // États pour les options (select, radio, checkbox)
  const [useColors, setUseColors] = useState(false)
  const [options, setOptions] = useState<Array<{
    id: string
    label: string
    value: string
    color: string
  }>>([{ id: '1', label: 'Option 1', value: 'option_1', color: '#3B82F6' }])
  const [originalOptionsCount, setOriginalOptionsCount] = useState(0)
  const [colorPickerOpen, setColorPickerOpen] = useState<string | null>(null)

  // États pour la modal de confirmation de suppression
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [fieldToDelete, setFieldToDelete] = useState<{
    field: (CustomField | CompanyCustomField) & { usage_count: number }
    type: 'contact' | 'company'
  } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Charger les champs personnalisés
  const loadContactCustomFields = async () => {
    try {
      const response = await apiService.getContactCustomFields()
      if (response.success && response.data) {
        setContactFields(response.data)
      }
    } catch (error) {
      console.error('Erreur lors du chargement des champs personnalisés des contacts:', error)
    }
  }

  const loadCompanyCustomFields = async () => {
    try {
      const response = await apiService.getCompanyCustomFieldsWithUsage()
      if (response.success && response.data) {
        setCompanyFields(response.data)
      }
    } catch (error) {
      console.error('Erreur lors du chargement des champs personnalisés des entreprises:', error)
    }
  }

  const loadAllCustomFields = async () => {
    setLoading(true)
    await Promise.all([
      loadContactCustomFields(),
      loadCompanyCustomFields()
    ])
    setLoading(false)
  }

  // Charger les données au montage
  useEffect(() => {
    loadAllCustomFields()
  }, [])

  // Effect pour gérer le focus quand la modale s'ouvre
  useEffect(() => {
    if (isCreateModalOpen && firstFieldRef.current) {
      const timer = setTimeout(() => {
        firstFieldRef.current?.focus()
        firstFieldRef.current?.select()
      }, 300)
      
      return () => clearTimeout(timer)
    }
  }, [isCreateModalOpen])

  // Gérer les touches clavier dans la modale
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isCreateModalOpen) return

      if (event.key === 'Escape') {
        handleOpenModal(false)
      } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        handleSubmitCustomField()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isCreateModalOpen])

  // Protection admin - rediriger si l'utilisateur n'est pas admin
  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />
  }

  // Gérer l'ouverture du modal avec pré-remplissage
  const handleOpenModal = (open: boolean) => {
    setIsCreateModalOpen(open)
    if (open && !isEditMode) {
      // Mode création : Pré-remplir "S'applique à" selon l'onglet actif
      setCreateForm(prev => ({
        ...prev,
        appliesTo: activeTab
      }))
      
      // Focus automatique après un délai synchronisé avec l'animation d'ouverture
      setTimeout(() => {
        firstFieldRef.current?.focus()
      }, 250)
    } else if (!open) {
      // Réinitialiser le formulaire quand on ferme
      setCreateForm({
        name: '',
        appliesTo: '',
        fieldType: '',
        required: false
      })
      setCreateErrors({})
      setIsEditMode(false)
      setFieldToEdit(null)
      // Réinitialiser les options
      setUseColors(false)
      setOptions([{ id: '1', label: 'Option 1', value: 'option_1', color: '#3B82F6' }])
      setOriginalOptionsCount(0)
    }
  }

  // Gérer l'édition d'un champ personnalisé
  const handleEditField = (field: (CustomField | CompanyCustomField) & { usage_count: number, options?: any[] }, type: 'contact' | 'company') => {
    setIsEditMode(true)
    setFieldToEdit(field)
    
    // Pré-remplir le formulaire avec les données existantes
    setCreateForm({
      name: field.name,
      appliesTo: type === 'contact' ? 'contacts' : 'companies',
      fieldType: field.type,
      required: field.required === 1
    })
    
    // Gérer les options si le champ en a
    if (field.options && field.options.length > 0) {
      setUseColors(field.color === 1)
      const formattedOptions = field.options.map((option, index) => ({
        id: option.id?.toString() || index.toString(),
        label: option.label,
        value: option.value || option.label.toLowerCase().replace(/\s+/g, '_'),
        color: option.color || '#3B82F6'
      }))
      setOptions(formattedOptions)
      setOriginalOptionsCount(formattedOptions.length)
    } else {
      setUseColors(false)
      setOptions([{ id: '1', label: 'Option 1', value: 'option_1', color: '#3B82F6' }])
      setOriginalOptionsCount(0)
    }
    
    setIsCreateModalOpen(true)
    
    // Focus automatique
    setTimeout(() => {
      firstFieldRef.current?.focus()
    }, 250)
  }

  // Fonctions pour gérer les options
  const addOption = () => {
    const newId = Date.now().toString()
    setOptions(prev => [...prev, {
      id: newId,
      label: `Option ${prev.length + 1}`,
      value: `option_${prev.length + 1}`,
      color: '#3B82F6'
    }])
  }

  const removeOption = (id: string) => {
    // En mode édition, on ne peut pas supprimer les options originales
    if (isEditMode) {
      const optionIndex = options.findIndex(option => option.id === id)
      if (optionIndex < originalOptionsCount) {
        toast({
          title: "Suppression interdite",
          description: "Vous ne pouvez pas supprimer une option existante car elle pourrait être utilisée.",
          variant: "destructive",
        })
        return
      }
    }
    
    if (options.length > 1) {
      setOptions(prev => prev.filter(option => option.id !== id))
    }
  }

  const updateOption = (id: string, field: string, value: string) => {
    setOptions(prev => prev.map(option => 
      option.id === id ? { ...option, [field]: value } : option
    ))
  }

  // Fonction pour obtenir le libellé du type de champ
  const getFieldTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'text': 'Texte',
      'number': 'Nombre', 
      'date': 'Date',
      'email': 'Email',
      'url': 'URL',
      'radio': 'Bouton radio',
      'checkbox': 'Case à cocher',
      'select': 'Liste déroulante'
    }
    return types[type] || type
  }

  // Métadonnées d'affichage (icône + couleur du badge) par type de champ
  const getFieldTypeMeta = (type: string): { Icon: typeof Type; badge: string } => {
    const meta: Record<string, { Icon: typeof Type; badge: string }> = {
      text: { Icon: Type, badge: 'bg-slate-100 text-slate-600' },
      number: { Icon: Hash, badge: 'bg-emerald-100 text-emerald-700' },
      date: { Icon: Calendar, badge: 'bg-amber-100 text-amber-700' },
      email: { Icon: Mail, badge: 'bg-cyan-100 text-cyan-700' },
      url: { Icon: LinkIcon, badge: 'bg-sky-100 text-sky-700' },
      radio: { Icon: CircleDot, badge: 'bg-blue-100 text-blue-700' },
      checkbox: { Icon: CheckSquare, badge: 'bg-teal-100 text-teal-700' },
      select: { Icon: List, badge: 'bg-violet-100 text-violet-700' },
    }
    return meta[type] || { Icon: Sliders, badge: 'bg-slate-100 text-slate-600' }
  }

  // Fonction pour rendre un champ personnalisé
  const renderFieldRow = (field: (CustomField | CompanyCustomField) & { usage_count: number, options?: any[] }, isCompany: boolean = false) => {
    const { Icon, badge } = getFieldTypeMeta(field.type)
    const usageLabel =
      field.usage_count === 0
        ? 'Non utilisé'
        : field.usage_count === 1
          ? `1 ${isCompany ? 'entreprise' : 'contact'}`
          : `${field.usage_count} ${isCompany ? 'entreprises' : 'contacts'}`

    return (
      <tr key={field.id} className="group hover:bg-gray-50 transition-colors">
        {/* Champ */}
        <td className="px-5 py-3 align-middle">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{field.name}</span>
            {field.required === 1 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                Obligatoire
              </span>
            )}
            {field.color === 1 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                Couleurs
              </span>
            )}
          </div>
        </td>
        {/* Type */}
        <td className="px-5 py-3 align-middle">
          <span className={`inline-flex items-center justify-center gap-1.5 w-40 px-2.5 py-1 rounded-full text-xs font-medium ${badge}`}>
            <Icon className="w-3.5 h-3.5 shrink-0" />
            {getFieldTypeLabel(field.type)}
          </span>
        </td>
        {/* Utilisation */}
        <td className="px-5 py-3 align-middle whitespace-nowrap text-sm text-gray-500">
          {usageLabel}
        </td>
        {/* Options */}
        <td className="px-5 py-3 align-middle">
          {field.options && field.options.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              {field.options.slice(0, 3).map((option, index) => (
                <span
                  key={option.id || index}
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs"
                >
                  {field.color === 1 && option.color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full border border-gray-300"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  {option.label}
                </span>
              ))}
              {field.options.length > 3 && (
                <span className="text-xs text-gray-400">+{field.options.length - 3}</span>
              )}
            </div>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
        {/* Actions */}
        <td className="px-5 py-3 align-middle">
          <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-green-600 hover:bg-green-50"
              onClick={() => handleEditField(field, isCompany ? 'company' : 'contact')}
              title="Modifier"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-red-600 hover:bg-red-50"
              onClick={() => handleDeleteField(field, isCompany ? 'company' : 'contact')}
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    )
  }

  // Conteneur tableau pour une liste de champs
  const renderFieldsTable = (
    fields: ((CustomField | CompanyCustomField) & { usage_count: number, options?: any[] })[],
    isCompany: boolean,
  ) => (
    <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
            <th className="px-5 py-3">Champ</th>
            <th className="px-5 py-3">Type</th>
            <th className="px-5 py-3">Utilisation</th>
            <th className="px-5 py-3">Options</th>
            <th className="px-5 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {fields.map(field => renderFieldRow(field, isCompany))}
        </tbody>
      </table>
    </div>
  )

  // Vérifier si le type de champ nécessite des options
  const requiresOptions = (fieldType: string) => {
    return ['select', 'radio', 'checkbox'].includes(fieldType)
  }

  // Gérer les changements dans le formulaire de création
  const handleCreateFormChange = (field: string, value: string | boolean) => {
    setCreateForm(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Effacer l'erreur pour ce champ
    if (createErrors[field]) {
      setCreateErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  // Gérer la création ou modification d'un champ personnalisé
  const handleSubmitCustomField = async () => {
    try {
      setIsCreating(true)
      setCreateErrors({})

      // Validation
      const errors: Record<string, string> = {}
      if (!createForm.name.trim()) {
        errors.name = 'Le nom du champ est requis'
      }
      if (!createForm.appliesTo) {
        errors.appliesTo = 'Veuillez sélectionner où s\'applique le champ'
      }
      if (!createForm.fieldType) {
        errors.fieldType = 'Veuillez sélectionner un type de champ'
      }

      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors)
        return
      }

      // Préparer les options si nécessaire
      let optionsToSend = undefined
      if (requiresOptions(createForm.fieldType)) {
        // Valider qu'il y a au moins une option
        if (options.length === 0) {
          setCreateErrors({ general: 'Au moins une option est requise pour ce type de champ' })
          return
        }

        // Valider que chaque option a un libellé
        for (const option of options) {
          if (!option.label.trim()) {
            setCreateErrors({ general: 'Chaque option doit avoir un libellé' })
            return
          }
        }

        optionsToSend = options.map(option => ({
          label: option.label.trim(),
          value: option.value.trim() || undefined,
          color: useColors ? option.color : undefined
        }))
      }

      const { apiService } = await import('@/services/api')
      let result

      if (isEditMode && fieldToEdit) {
        // Mode édition : préparer les nouvelles options (en conservant les ID des options existantes)
        let optionsForUpdate = undefined
        if (requiresOptions(createForm.fieldType)) {
          // En mode édition, on distingue les options existantes des nouvelles
          optionsForUpdate = options.map((option, index) => {
            if (index < originalOptionsCount) {
              // Option existante : garder l'ID original si disponible
              const originalOption = fieldToEdit.options?.[index]
              return {
                id: originalOption?.id || option.id,
                label: option.label.trim(),
                value: option.value.trim() || undefined,
                color: useColors ? option.color : undefined
              }
            } else {
              // Nouvelle option : pas d'ID (sera créée)
              return {
                label: option.label.trim(),
                value: option.value.trim() || undefined,
                color: useColors ? option.color : undefined
              }
            }
          })
        }

        // Appel API pour modification
        if (createForm.appliesTo === 'contacts') {
          result = await apiService.updateCustomField(fieldToEdit.id, {
            name: createForm.name.trim(),
            required: createForm.required,
            color: useColors,
            options: optionsForUpdate
          })
        } else if (createForm.appliesTo === 'companies') {
          result = await apiService.updateCompanyCustomField(fieldToEdit.id, {
            name: createForm.name.trim(),
            required: createForm.required,
            color: useColors,
            options: optionsForUpdate
          })
        }
      } else {
        // Mode création
        if (createForm.appliesTo === 'contacts') {
          result = await apiService.createCustomField({
            name: createForm.name.trim(),
            type: createForm.fieldType as any, // Cast car on a validé le type
            required: createForm.required,
            color: useColors, // Utiliser des couleurs si coché
            options: optionsToSend
          })
        } else if (createForm.appliesTo === 'companies') {
          result = await apiService.createCompanyCustomField({
            name: createForm.name.trim(),
            type: createForm.fieldType as any, // Cast car on a validé le type
            required: createForm.required,
            color: useColors, // Utiliser des couleurs si coché
            text_case: undefined, // Valeur par défaut
            options: optionsToSend
          })
        } else {
          setCreateErrors({ general: 'Type non supporté' })
          return
        }
      }

      if (result?.success) {
      // Fermer le modal (qui réinitialisera automatiquement le formulaire)
      handleOpenModal(false)
      
        // Recharger les champs personnalisés
        await loadAllCustomFields()
        
        // Toast de succès (vert)
        toast({
          title: isEditMode ? "Champ modifié avec succès" : "Champ créé avec succès",
          description: `Le champ "${createForm.name}" a été ${isEditMode ? 'modifié' : 'créé'} pour les ${createForm.appliesTo === 'contacts' ? 'contacts' : 'entreprises'}.`,
          variant: "success",
        })
        
        console.log(`Champ personnalisé ${isEditMode ? 'modifié' : 'créé'} avec succès:`, result.data)
      } else {
        setCreateErrors({ general: result?.error || `Erreur lors de la ${isEditMode ? 'modification' : 'création'} du champ` })
        
        // Toast d'erreur
        toast({
          title: `Erreur lors de la ${isEditMode ? 'modification' : 'création'}`,
          description: result?.error || `Une erreur est survenue lors de la ${isEditMode ? 'modification' : 'création'} du champ.`,
          variant: "destructive",
        })
      }
      
    } catch (error) {
      console.error('Erreur lors de la création du champ personnalisé:', error)
      setCreateErrors({ general: 'Erreur de connexion au serveur' })
      
      // Toast d'erreur
      toast({
        title: "Erreur de connexion",
        description: 'Impossible de se connecter au serveur. Veuillez réessayer.',
        variant: "destructive",
      })
    } finally {
      setIsCreating(false)
    }
  }

  // Ouvrir la modal de confirmation de suppression
  const handleDeleteField = (field: (CustomField | CompanyCustomField) & { usage_count: number }, type: 'contact' | 'company') => {
    setFieldToDelete({ field, type })
    setIsDeleteModalOpen(true)
  }

  // Confirmer la suppression du champ personnalisé
  const confirmDeleteField = async () => {
    if (!fieldToDelete) return

    try {
      setIsDeleting(true)
      
      let response
      if (fieldToDelete.type === 'contact') {
        response = await apiService.deleteCustomField(fieldToDelete.field.id)
      } else {
        response = await apiService.deleteCompanyCustomField(fieldToDelete.field.id)
      }

      if (response.success) {
        await loadAllCustomFields() // Recharger la liste
        setIsDeleteModalOpen(false)
        setFieldToDelete(null)
        
        toast({
          variant: "success",
          title: "✅ Champ supprimé",
          description: `Le champ "${fieldToDelete.field.name}" a été supprimé avec succès`
        })
      } else {
        toast({
          variant: "destructive",
          title: "Erreur de suppression",
          description: response.error || "Impossible de supprimer le champ personnalisé"
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: "Impossible de supprimer le champ personnalisé. Vérifiez votre connexion."
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Annuler la suppression
  const cancelDeleteField = () => {
    setIsDeleteModalOpen(false)
    setFieldToDelete(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Sliders className="w-6 h-6 text-blue-600" />
            <span>Champs personnalisés</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez vos champs personnalisés pour contacts et entreprises
          </p>
        </div>
        <div className="flex items-center space-x-3">
          
          {/* Modal d'ajout de champ personnalisé */}
          <Dialog open={isCreateModalOpen} onOpenChange={handleOpenModal}>
            <DialogTrigger asChild>
              <Button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4" />
                <span>Ajouter un champ personnalisé</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col mx-auto my-auto animate-in fade-in-0 zoom-in-95 duration-200">
              <DialogHeader className="flex-shrink-0 pb-4">
                <DialogTitle>{isEditMode ? 'Modifier le champ personnalisé' : 'Ajouter un champ personnalisé'}</DialogTitle>
                <DialogDescription>
                  {isEditMode 
                    ? 'Modifiez les propriétés de votre champ personnalisé. Vous pouvez ajouter des options mais pas en supprimer.'
                    : 'Créez un nouveau champ personnalisé pour vos contacts ou entreprises'
                  }
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-2 overflow-y-auto flex-1 px-1">
                {/* Nom du champ */}
                <div className="space-y-2">
                  <Label htmlFor="field-name">
                    Nom du champ *
                  </Label>
                  <Input
                    id="field-name"
                    placeholder="ex: dateNaissance"
                    value={createForm.name}
                    onChange={(e) => handleCreateFormChange('name', e.target.value)}
                    className={createErrors.name ? 'border-red-500' : ''}
                    ref={firstFieldRef}
                  />
                  <p className="text-xs text-gray-500">
                    Utilisez un nom simple sans espaces (ex: "dateNaissance")
                  </p>
                  {createErrors.name && (
                    <p className="text-red-500 text-xs">{createErrors.name}</p>
                  )}
                </div>

                {/* S'applique à */}
                <div className="space-y-2">
                  <Label htmlFor="applies-to">
                    S'applique à *
                  </Label>
                  <Select 
                    value={createForm.appliesTo || activeTab} 
                    onValueChange={(value) => handleCreateFormChange('appliesTo', value)}
                    disabled={isEditMode}
                  >
                    <SelectTrigger className={`${createErrors.appliesTo ? 'border-red-500' : ''} ${isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <SelectValue placeholder={activeTab === 'contacts' ? 'Contacts' : 'Entreprises'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contacts">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>Contacts</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="companies">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4" />
                          <span>Entreprises</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {createErrors.appliesTo && (
                    <p className="text-red-500 text-xs">{createErrors.appliesTo}</p>
                  )}
                </div>

                {/* Type de champ */}
                <div className="space-y-2">
                  <Label htmlFor="field-type">
                    Type de champ *
                  </Label>
                  <Select 
                    value={createForm.fieldType} 
                    onValueChange={(value) => handleCreateFormChange('fieldType', value)}
                    disabled={isEditMode}
                  >
                    <SelectTrigger className={`${createErrors.fieldType ? 'border-red-500' : ''} ${isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Texte</SelectItem>
                      <SelectItem value="number">Nombre</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="url">URL</SelectItem>
                      <SelectItem value="radio">Case à cocher unique (Radio)</SelectItem>
                      <SelectItem value="checkbox">Case à cocher multiple (Checkbox)</SelectItem>
                      <SelectItem value="select">Liste déroulante (Select)</SelectItem>
                    </SelectContent>
                  </Select>
                  {createErrors.fieldType && (
                    <p className="text-red-500 text-xs">{createErrors.fieldType}</p>
                  )}
                </div>

                {/* Options pour select, radio, checkbox */}
                {requiresOptions(createForm.fieldType) && (
                  <div className="space-y-4">
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="text-base font-medium">
                          Options *
                        </Label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="use-colors"
                            checked={useColors}
                            onChange={(e) => setUseColors(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <Label htmlFor="use-colors" className="text-sm font-normal cursor-pointer">
                            Utiliser des couleurs
                          </Label>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {options.map((option, index) => {
                          const isOriginalOption = isEditMode && index < originalOptionsCount
                          const canDelete = !isOriginalOption && options.length > 1
                          
                          return (
                            <div key={option.id} className={`border rounded-lg p-3 space-y-3 ${isOriginalOption ? 'bg-blue-50/30 border-blue-200' : 'bg-white'}`}>
                              {isOriginalOption && (
                                <div className="text-xs text-blue-600 mb-2 flex items-center">
                                  <span className="mr-1">✏️</span>
                                  Option existante (modifiable, non supprimable)
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-sm">Libellé</Label>
                                  <Input
                                    placeholder="ex: Option 1"
                                    value={option.label}
                                    onChange={(e) => updateOption(option.id, 'label', e.target.value)}
                                    className="mt-1"
                                  />
                                </div>
                                <div className="flex items-end">
                                  <div className="flex-1">
                                    <Label className="text-sm">Valeur (optionnel)</Label>
                                    <Input
                                      placeholder="ex: option_1"
                                      value={option.value}
                                      onChange={(e) => updateOption(option.id, 'value', e.target.value)}
                                      className="mt-1"
                                    />
                                  </div>
                                  {canDelete && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeOption(option.id)}
                                      className="ml-2 h-10 w-10 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      title="Supprimer cette option"
                                    >
                                      ×
                                    </Button>
                                  )}
                                  {isOriginalOption && (
                                    <div className="ml-2 h-10 w-10 flex items-center justify-center text-gray-400" title="Option existante - suppression non autorisée">
                                      🔒
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {useColors && (
                                <div>
                                  <Label className="text-sm">Couleur</Label>
                                  <div className="space-y-2 mt-1">
                                    {/* Input hexadécimal avec aperçu et bouton pour ouvrir la palette */}
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        className="w-8 h-8 rounded border border-gray-300 flex-shrink-0 hover:border-gray-400 transition-colors cursor-pointer"
                                        style={{ backgroundColor: option.color }}
                                        onClick={() => setColorPickerOpen(colorPickerOpen === option.id ? null : option.id)}
                                        title="Cliquer pour ouvrir/fermer la palette de couleurs"
                                      />
                                      <Input
                                        type="text"
                                        value={option.color}
                                        onChange={(e) => updateOption(option.id, 'color', e.target.value)}
                                        placeholder="#3B82F6"
                                        className="flex-1 font-mono text-sm"
                                        pattern="^#[0-9A-Fa-f]{6}$"
                                      />
                                    </div>
                                    
                                    {/* Palette de couleurs prédéfinies - affichée seulement si ouverte */}
                                    {colorPickerOpen === option.id && (
                                      <div className="grid grid-cols-8 gap-2 p-3 bg-gray-50 rounded-lg border">
                                        {[
                                          '#EF4444', '#F97316', '#F59E0B', '#EAB308',
                                          '#84CC16', '#22C55E', '#10B981', '#14B8A6',
                                          '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
                                          '#8B5CF6', '#A855F7', '#D946EF', '#EC4899',
                                          '#F43F5E', '#64748B', '#6B7280', '#374151',
                                          '#1F2937', '#111827', '#000000', '#FFFFFF'
                                        ].map((color) => (
                                          <button
                                            key={color}
                                            type="button"
                                            className={`w-8 h-8 rounded border-2 transition-all hover:scale-110 ${
                                              option.color === color 
                                                ? 'border-gray-900 shadow-lg' 
                                                : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => {
                                              updateOption(option.id, 'color', color)
                                              setColorPickerOpen(null) // Fermer la palette après sélection
                                            }}
                                            title={color}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })}

                        <Button
                          type="button"
                          variant="outline"
                          onClick={addOption}
                          className="w-full border-dashed"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Ajouter une option
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Champs obligatoire */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="field-required"
                    checked={createForm.required}
                    onChange={(e) => handleCreateFormChange('required', e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <Label htmlFor="field-required" className="text-sm font-normal cursor-pointer">
                    Champs obligatoire
                  </Label>
                </div>
              </div>

              {/* Erreur générale */}
              {createErrors.general && (
                <div className="text-red-500 text-sm mt-2 p-3 bg-red-50 border border-red-200 rounded flex-shrink-0">
                  {createErrors.general}
                </div>
              )}

              <DialogFooter className="flex-shrink-0 pt-4 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  onClick={() => handleOpenModal(false)}
                  disabled={isCreating}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleSubmitCustomField}
                  disabled={isCreating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isCreating ? (isEditMode ? 'Modification...' : 'Création...') : (isEditMode ? 'Modifier le champ' : 'Créer le champ')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'contacts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Champs pour contacts</span>
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
              activeTab === 'companies'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span>Champs pour entreprises</span>
          </button>
        </nav>
      </div>

      {/* Contenu principal - État vide */}
      {/* Contenu principal */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Chargement des champs personnalisés...</p>
          </div>
        </div>
      ) : (
        <div>
          {/* Affichage des champs selon l'onglet actif */}
          {activeTab === 'contacts' ? (
            contactFields.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="text-center max-w-md">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                    <Settings className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Aucun champ personnalisé
                  </h3>
                  <p className="text-gray-500 mb-8">
                    Commencez par créer votre premier champ personnalisé pour les contacts.
                  </p>
                  <div className="flex justify-center">
                    <Dialog open={isCreateModalOpen} onOpenChange={handleOpenModal}>
                      <DialogTrigger asChild>
                        <Button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4" />
                          <span>Ajouter un champ</span>
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                  </div>
                </div>
              </div>
            ) : (
              renderFieldsTable(contactFields, false)
            )
          ) : (
            companyFields.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-20 px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <Settings className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucun champ personnalisé
          </h3>
          <p className="text-gray-500 mb-8">
                    Commencez par créer votre premier champ personnalisé pour les entreprises.
          </p>
          <div className="flex justify-center">
            <Dialog open={isCreateModalOpen} onOpenChange={handleOpenModal}>
              <DialogTrigger asChild>
                <Button className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4" />
                  <span>Ajouter un champ</span>
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>
        </div>
      </div>
            ) : (
              renderFieldsTable(companyFields, true)
            )
          )}
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              <span>Confirmer la suppression</span>
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce champ personnalisé ? Cette action est irréversible et supprimera toutes les données associées.
            </DialogDescription>
          </DialogHeader>
          
          {fieldToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {fieldToDelete.field.name}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Type : {getFieldTypeLabel(fieldToDelete.field.type)}
                </p>
                <p className="text-sm text-gray-600">
                  Utilisation : {fieldToDelete.field.usage_count === 0
                    ? 'Non utilisé'
                    : fieldToDelete.field.usage_count === 1
                    ? `1 ${fieldToDelete.type === 'company' ? 'entreprise' : 'contact'}`
                    : `${fieldToDelete.field.usage_count} ${fieldToDelete.type === 'company' ? 'entreprises' : 'contacts'}`
                  }
                </p>
                {fieldToDelete.field.usage_count > 0 && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
                    ⚠️ Attention : Supprimer ce champ effacera toutes les valeurs existantes pour ce champ.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={cancelDeleteField}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteField}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
